import { type FC, type MouseEvent, useEffect, useMemo, useState } from 'react';
import type { ExternalToast } from 'sonner';

import type {
  NotificationAchievementPayloadDto,
  NotificationRematchPayloadDto,
} from '@skillarea/lib-shared';

import { Icon } from '../icons/Icon';

export type TToastAction = {
  label: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
};

function parseDateMs(value: string | undefined): number | undefined {
  if (!value) {
    return;
  }

  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return;
  }

  return parsed;
}

function formatCountdownMs(remainingMs: number): string {
  const clampedMs = Number.isFinite(remainingMs) ? Math.max(0, remainingMs) : 0;
  const totalSeconds = Math.ceil(clampedMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
}

const RematchExpiryCountdown: FC<{
  expiresAt?: string;
}> = ({ expiresAt }) => {
  const expiresAtMs = useMemo(() => parseDateMs(expiresAt), [expiresAt]);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (expiresAtMs === undefined) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [expiresAtMs]);

  if (expiresAtMs === undefined) {
    return null;
  }

  const remainingMs = Math.max(0, expiresAtMs - nowMs);

  return (
    <div className="flex min-w-0 items-center gap-2">
      <Icon aria-hidden="true" img="watch" size="s" color="accent" />
      <span className="min-w-0 truncate text-foreground">
        Expires in {formatCountdownMs(remainingMs)}
      </span>
    </div>
  );
};

export const AchievementToastDescription: FC<{
  payload: NotificationAchievementPayloadDto;
}> = ({ payload }) => {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2 break-words">
      <span className="min-w-0 text-foreground">{payload.achievementName}</span>
      <span className="inline-flex items-center gap-1 rounded-md border border-border bg-background/30 px-2 py-1">
        <Icon aria-hidden="true" img="coin" size="s" color="accent" />
        <span className="[font-family:var(--font-button)] text-[10px] leading-none text-accent">
          +{payload.coins}
        </span>
      </span>
    </div>
  );
};

export const RematchToastDescription: FC<{
  payload: NotificationRematchPayloadDto;
  expiresAt?: string;
}> = ({ payload, expiresAt }) => {
  return (
    <div className="grid min-w-0 gap-1">
      <div className="flex min-w-0 items-center gap-2">
        <Icon aria-hidden="true" img="players" size="s" color="accent" />
        <span className="text-muted-foreground">From</span>
        <span className="min-w-0 truncate font-semibold text-accent">
          {payload.fromUsername}
        </span>
      </div>

      <div className="flex min-w-0 items-center gap-2">
        <Icon aria-hidden="true" img="game" size="s" color="accent" />
        <span className="text-muted-foreground">Game</span>
        <span className="min-w-0 truncate text-foreground">
          {payload.gameName}
        </span>
      </div>

      <RematchExpiryCountdown expiresAt={expiresAt} />
    </div>
  );
};

export function createAchievementToastOptions(
  payload: NotificationAchievementPayloadDto,
  options?: {
    icon?: ExternalToast['icon'];
    action?: ExternalToast['action'];
  },
): ExternalToast {
  return {
    icon: options?.icon ?? (
      <Icon aria-hidden="true" img="star" size="m" color="accent" />
    ),
    description: <AchievementToastDescription payload={payload} />,
    action: options?.action,
  };
}

export function createRematchToastOptions(
  payload: NotificationRematchPayloadDto,
  actions: {
    accept: TToastAction;
    decline: TToastAction;
  },
  options?: {
    icon?: ExternalToast['icon'];
    expiresAt?: string;
  },
): ExternalToast {
  return {
    icon: options?.icon ?? (
      <Icon aria-hidden="true" img="watch" size="m" color="accent" />
    ),
    description: (
      <RematchToastDescription
        payload={payload}
        expiresAt={options?.expiresAt}
      />
    ),
    action: actions.accept,
    cancel: actions.decline,
  };
}
